import { cn } from "@/lib/utils";
import type { BadgeSize, BadgeVariant } from "@/types/ui";
import { cva } from "class-variance-authority";
import type { HTMLAttributes, ReactNode } from "react";

const badgeVariants = cva("inline-flex items-center rounded-full font-semibold transition-colors duration-200", {
  variants: {
    variant: {
      default: "bg-primary/10 text-primary hover:bg-primary/20",
      secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
      destructive: "bg-destructive/10 text-destructive hover:bg-destructive/20",
      success: "bg-success/10 text-success hover:bg-success/20",
      paid: "bg-brand-purple/12 text-brand-purple hover:bg-brand-purple/20",
      warning: "bg-warning/10 text-warning hover:bg-warning/20",
      info: "bg-info/10 text-info hover:bg-info/20",
    },
    size: {
      sm: "px-2 py-0.5 text-xs",
      default: "px-2.5 py-0.5 text-xs",
      lg: "px-3 py-1 text-sm",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
  },
});

export interface BadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, "variant"> {
  children?: ReactNode;
  variant?: BadgeVariant;
  size?: BadgeSize;
  dot?: boolean;
}

const Badge = ({ className, children, variant = "default", size = "default", dot = false, ...props }: BadgeProps) => {
  return (
    <span
      className={cn(
        badgeVariants({ variant, size, className }),
        dot && "before:mr-1.5 before:inline-block before:h-1.5 before:w-1.5 before:rounded-full before:bg-current",
      )}
      {...props}
    >
      {children}
    </span>
  );
};

export { Badge };
